/**
 * Pinned folders: a sort order. A pinned folder is listed first in the folder that directly contains
 * it, and nowhere else. The list is stored per user by JobService (`GET/PUT /v1/me/pins`,
 * `JobService/docs/shared-folders.md` in omicscloud-cdk), so the web and the desktop show the same.
 *
 * A pin names one folder:
 * - `{kind: 'folder', path: 'A/B/'}`: a folder of My Files (`B` comes first inside `A/`);
 * - `{kind: 'share', shareId}`: a shared folder, first in the Shared list;
 * - `{kind: 'share', shareId, path: 'a/b/'}`: a folder inside that share, first in its parent there.
 *
 * Share pins keep working when the owner moves or renames the shared folder. Folder pins, and share
 * pins inside a share, are rewritten here when the user moves or renames the folder ({@link movePins}).
 * A pin whose folder is gone simply sorts nothing.
 */

/** A folder of My Files. `path` is relative to My Files and ends with `/`: `A/B/`. */
export interface FolderPin {
  kind: 'folder'
  path: string
}

/** A shared folder, by its share id; with `path`, a folder inside it (relative to it, ending in `/`). */
export interface SharePin {
  kind: 'share'
  shareId: string
  path?: string
}

export type Pin = FolderPin | SharePin

/** What the server keeps: at most this many pins per user. */
export const MAX_PINS = 50

export const folderPin = (path: string): FolderPin => ({kind: 'folder', path})
export const sharePin = (shareId: string, path?: string): SharePin =>
  path ? {kind: 'share', shareId, path} : {kind: 'share', shareId}

/** Same pin: the same folder of My Files, or the same folder of the same share. */
export function samePin(a: Pin, b: Pin): boolean {
  if (a.kind === 'folder') {
    return b.kind === 'folder' && a.path === b.path
  }
  return b.kind === 'share' && a.shareId === b.shareId && (a.path ?? '') === (b.path ?? '')
}

export const isPinned = (pins: readonly Pin[], pin: Pin): boolean =>
  pins.some((other) => samePin(other, pin))

/** The list in order, without repeats (the first one stays). */
export function uniquePins(pins: readonly Pin[]): Pin[] {
  return pins.filter((pin, index) => pins.findIndex((other) => samePin(other, pin)) === index)
}

/** The list with `pin` added at the end, unless it is there already. */
export const withPin = (pins: readonly Pin[], pin: Pin): Pin[] =>
  isPinned(pins, pin) ? [...pins] : [...pins, pin]

/** The list without any of `remove`. */
export const withoutPins = (pins: readonly Pin[], remove: readonly Pin[]): Pin[] =>
  pins.filter((pin) => !isPinned(remove, pin))

/** Same pins, in the same order. */
export const samePins = (a: readonly Pin[], b: readonly Pin[]): boolean =>
  a.length === b.length && a.every((pin, index) => samePin(pin, b[index]))

/** A folder path as a pin holds it: no leading slash, one trailing slash. Empty for the root. */
const asFolderPath = (path: string): string => {
  const trimmed = path.replace(/^\/+/, '').replace(/\/+$/, '')
  return trimmed === '' ? '' : `${trimmed}/`
}

/**
 * Keeps the pins the server could have sent and drops anything else, so a malformed answer can never
 * break the Files browser. Repeats are dropped too.
 */
export function normalizePins(raw: unknown): Pin[] {
  if (!Array.isArray(raw)) {
    return []
  }
  const pins: Pin[] = []
  for (const item of raw) {
    if (!item || typeof item !== 'object') {
      continue
    }
    const {kind, path, shareId} = item as {kind?: unknown; path?: unknown; shareId?: unknown}
    if (kind === 'folder' && typeof path === 'string' && asFolderPath(path) !== '') {
      pins.push(folderPin(asFolderPath(path)))
    } else if (kind === 'share' && typeof shareId === 'string' && shareId !== '') {
      if (path === undefined || path === null || path === '') {
        pins.push(sharePin(shareId))
      } else if (typeof path === 'string' && asFolderPath(path) !== '') {
        pins.push(sharePin(shareId, asFolderPath(path)))
      }
    }
  }
  return uniquePins(pins)
}

/** A folder that went somewhere else: both paths end with `/`. */
export interface FolderMove {
  from: string
  to: string
}

/**
 * The pins after folders moved or were renamed: a pin at a moved folder, or anywhere below it,
 * follows it. Without `shareId` the moves are in My Files and rewrite folder pins; with it they are
 * inside that share and rewrite its pins with a path. A shared folder's own pin never changes.
 */
export function movePins(
  pins: readonly Pin[],
  moves: readonly FolderMove[],
  shareId?: string
): Pin[] {
  const follow = (path: string) => {
    const move = moves.find(({from}) => from !== '' && path.startsWith(from))
    return move ? `${move.to}${path.slice(move.from.length)}` : path
  }
  const rebased = pins.map((pin): Pin => {
    if (shareId === undefined) {
      return pin.kind === 'folder' ? folderPin(follow(pin.path)) : pin
    }
    return pin.kind === 'share' && pin.shareId === shareId && pin.path
      ? sharePin(shareId, follow(pin.path))
      : pin
  })
  return uniquePins(rebased)
}

/** A rename in place: `a/old/` to `a/new/`. Nothing for a file. */
export function renameMoves(source: string, destination: string): FolderMove[] {
  return source.endsWith('/') ? [{from: source, to: asFolderPath(destination)}] : []
}

/**
 * A move into `destinationFolder` (`''` for the top, `a/b` or `a/b/` below it): each folder among
 * `sources` keeps its name there. Files are left out.
 */
export function intoFolderMoves(
  sources: readonly string[],
  destinationFolder: string
): FolderMove[] {
  const destination = asFolderPath(destinationFolder)
  return sources
    .filter((source) => source.endsWith('/'))
    .map((source) => {
      const name = source.replace(/\/$/, '').split('/').pop() ?? ''
      return {from: source, to: `${destination}${name}/`}
    })
    .filter(({from, to}) => from !== to)
}
